import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FolderKanban, FlaskConical, Clock, Link2, CheckCircle2,
  MoreHorizontal, CircleDot, Circle, ArrowLeft,
} from 'lucide-react';
import api from '../services/api';

const toCamelCase = (obj) => {
  if (!obj) return null;
  const out = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
    out[camelKey] = obj[key];
  }
  return out;
};

function Field({ label, value }) {
  const empty = !value;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: empty ? 400 : 500, color: empty ? '#94a3b8' : '#0f172a', fontStyle: empty ? 'italic' : 'normal', textAlign: 'right', maxWidth: '60%' }}>
        {empty ? 'Not set yet' : value}
      </span>
    </div>
  );
}

function SectionCard({ icon: Icon, iconBg, title, children }) {
  return (
    <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} color="#fff" />
        </div>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{title}</span>
      </div>
      <div style={{ padding: '4px 20px 12px' }}>
        {children}
      </div>
    </div>
  );
}

function Timeline({ steps }) {
  return (
    <SectionCard icon={Clock} iconBg="#3b82f6" title="Timeline">
      <div style={{ paddingTop: 8 }}>
        {steps.map((step, i) => {
          const done = !!step.value;
          const isLast = i === steps.length - 1;
          return (
            <div key={step.label} style={{ display: 'flex' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {done
                  ? <CircleDot size={16} color="#2563eb" />
                  : <Circle size={16} color="#cbd5e1" />}
                {!isLast && (
                  <div style={{ width: 1, flex: 1, minHeight: 22, background: done ? '#bfdbfe' : '#e2e8f0', margin: '2px 0' }} />
                )}
              </div>
              <div style={{ marginLeft: 12, paddingBottom: isLast ? 4 : 16 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: done ? '#0f172a' : '#94a3b8' }}>{step.label}</p>
                <p style={{ margin: 0, fontSize: 12, color: done ? '#2563eb' : '#94a3b8', fontStyle: done ? 'normal' : 'italic' }}>
                  {step.value || 'Not set yet'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

function SegView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { project } = location.state || {};
  const [fullData, setFullData] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  const segmentId = project?.id;

  useEffect(() => {
    if (!segmentId) return;
    api.get(`api/segments/by-id/${segmentId}/`)
      .then(res => setFullData(toCamelCase(res.data)))
      .catch(() => setFetchError(true));
  }, [segmentId]);

  const p = fullData || (project ? toCamelCase(project) : null);

  if (!p) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#94a3b8', fontSize: 15 }}>
        No project data available.
      </div>
    );
  }

  const timelineSteps = [
    { label: 'Date of AWN', value: p.dateOfAwn },
    { label: 'Date of revised AWN', value: p.dateOfRevisedAwn },
    { label: 'Final R-AWN date', value: p.finalAwnDate },
    { label: 'Questionnaire received', value: p.qnrReceivedDate },
    { label: 'AP received date', value: p.apReceivedDate },
    { label: 'FW start date', value: p.fwStartDate },
    { label: 'FW end date', value: p.fwEndDate },
    { label: 'Top lines date', value: p.latestToplineDate },
    { label: 'Expected final output', value: p.expectedDateOfFinalOutput },
    { label: 'Actual final output', value: p.actualDateOfFinalOutput },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px 28px' }}>

      {/* Back + Title */}
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, fontWeight: 500, padding: 0, marginBottom: 10, fontFamily: 'inherit' }}
        >
          <ArrowLeft size={15} /> Back
        </button>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: -0.3 }}>
          {p.projectName || 'Project Detail'}
        </h1>
        {p.jobNumber && (
          <span style={{ fontSize: 13, color: '#64748b', marginTop: 2, display: 'block' }}>
            {p.jobNumber}{p.segmentName ? ` · ${p.segmentName}` : ''}
          </span>
        )}
      </div>

      {/* Two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <SectionCard icon={FolderKanban} iconBg="#6366f1" title="Project Details">
            <Field label="Segment name" value={p.segmentName} />
            <Field label="Unit" value={p.unit} />
            <Field label="Year" value={p.year} />
            <Field label="Month" value={p.month} />
            <Field label="Job number" value={p.jobNumber} />
            <Field label="Segment count" value={p.noOfSegments || p.segmentNumber} />
          </SectionCard>

          <Timeline steps={timelineSteps} />
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <SectionCard icon={FlaskConical} iconBg="#10b981" title="Research and Analysis">
            <Field label="Research group head" value={p.researchGroupHead} />
            <Field label="Contact in research" value={p.contactPersonInResearch} />
            <Field label="Contact in scripting" value={p.contactPersonForScripting} />
            <Field label="Contact in DP" value={p.contactPersonForDp} />
            <Field label="Actual sample size" value={p.actualSampleSize} />
            <Field label="Achieved sample size" value={p.achievedSampleSize} />
            <Field label="Final processed samples" value={p.finalProcessedSamples} />
          </SectionCard>

          <SectionCard icon={Link2} iconBg="#0ea5e9" title="Platforms">
            <Field label="Survey platform" value={p.surveyPlatform} />
            <Field label="DP platform" value={p.dpPlatform} />
          </SectionCard>

          <SectionCard icon={CheckCircle2} iconBg="#f59e0b" title="Status">
            <Field label="Current status" value={p.status} />
            <Field label="Remarks" value={p.remarks} />
            <Field label="Project classification" value={p.projectClassification} />
            <Field label="Backup folder path" value={p.backupFolderPath} />
          </SectionCard>

          <SectionCard icon={MoreHorizontal} iconBg="#94a3b8" title="Others">
            <Field label="Advanced analytics" value={p.advancedAnalytics} />
            <Field label="Number of top lines" value={p.noOfTopLines} />
            <Field label="OE coding" value={p.oeCoding} />
            <Field label="OE costing" value={p.oeCosting} />
            <Field label="Number of OE responses" value={p.numberOfResponses} />
            <Field label="Norms done" value={p.normsDone} />
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

export default SegView;
